import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Plus, Save, Trash2, Wand2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { cn, formatMw, formatNumber, formatPrice } from "../lib/utils";

export const Route = createFileRoute("/forecasting/contracts")({
  component: ForecastContractsRoute,
});

type ProfileKind = "BASELOAD" | "PEAKLOAD" | "OFFPEAK" | "SOLAR" | "BESS" | "CUSTOM";
type ForecastTimezone = "CET" | "EET";
type ContractSide = "BUY" | "SELL";
type ContractStatus = "DRAFT" | "ACTIVE" | "ARCHIVED";
type Currency = "EUR" | "RON";
type ProfileValues = number[][];
type ForecastContractsTab = "contracts" | "profiles" | "month";
type MonthViewMode = "table" | "column" | "contracts";
type MonthMetric = "net" | "buy" | "sell";
type ContractBookScope = "active" | "draft-active" | "all";

interface ForecastProfile {
  id: string;
  name: string;
  kind: ProfileKind;
  timezone: ForecastTimezone;
  values: ProfileValues;
  description: string | null;
  loadFactor: number;
  activeHoursPerWeek: number;
  updatedAt: string;
}

interface ForecastContract {
  id: string;
  name: string;
  counterparty: string;
  side: ContractSide;
  status: ContractStatus;
  profileId: string | null;
  profileName: string | null;
  profileKind: ProfileKind | null;
  portfolioId: string | null;
  portfolioBrokerCode: string | null;
  deliveryStartDate: string;
  deliveryEndDate: string;
  mw: number;
  price: number;
  currency: Currency;
  area: string;
  businessLine: string | null;
  notes: string | null;
  estimatedWeeklyMwh: number | null;
  updatedAt: string;
}

interface PortfolioOption {
  id: string;
  brokerCode: string;
  shortName: string | null;
  market: string;
  area: string;
}

type ProfileFormState = {
  id: string | null;
  name: string;
  kind: ProfileKind;
  timezone: ForecastTimezone;
  values: ProfileValues;
  description: string;
};

type ContractFormState = {
  id: string | null;
  name: string;
  counterparty: string;
  side: ContractSide;
  status: ContractStatus;
  profileId: string;
  portfolioId: string;
  deliveryStartDate: string;
  deliveryEndDate: string;
  mw: string;
  price: string;
  currency: Currency;
  area: string;
  businessLine: string;
  notes: string;
};

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const PROFILE_KINDS: ProfileKind[] = ["BASELOAD", "PEAKLOAD", "OFFPEAK", "SOLAR", "BESS", "CUSTOM"];
const BRUSH_VALUES = [0, 0.25, 0.5, 0.75, 1];
const MONTH_VIEW_MODES: Array<{ value: MonthViewMode; label: string }> = [
  { value: "table", label: "24 x days" },
  { value: "column", label: "Column" },
  { value: "contracts", label: "Contracts" },
];
const MONTH_METRICS: Array<{ value: MonthMetric; label: string }> = [
  { value: "net", label: "Net" },
  { value: "buy", label: "Buy" },
  { value: "sell", label: "Sell" },
];

function ForecastContractsRoute() {
  return <ForecastingBookPage view="contracts" />;
}

export function ForecastingBookPage({ view }: { view: ForecastContractsTab }) {
  const queryClient = useQueryClient();
  const [profileForm, setProfileForm] = useState<ProfileFormState>(() => newProfileForm("BASELOAD"));
  const [contractForm, setContractForm] = useState<ContractFormState>(() => newContractForm());

  const profilesQuery = useQuery({
    queryKey: ["forecasting", "profiles"],
    queryFn: () => api<{ profiles: ForecastProfile[] }>("/forecasting/profiles"),
  });
  const contractsQuery = useQuery({
    queryKey: ["forecasting", "contracts"],
    queryFn: () => api<{ contracts: ForecastContract[] }>("/forecasting/contracts?limit=1000"),
  });
  const portfoliosQuery = useQuery({
    queryKey: ["portfolios"],
    queryFn: () => api<{ portfolios: PortfolioOption[] }>("/portfolios"),
  });

  const profiles = profilesQuery.data?.profiles ?? [];
  const contracts = contractsQuery.data?.contracts ?? [];
  const portfolios = portfoliosQuery.data?.portfolios ?? [];
  const stats = useMemo(() => contractStats(contracts, profiles), [contracts, profiles]);
  const page = forecastingBookPageMeta(view);

  const saveProfile = useMutation({
    mutationFn: (form: ProfileFormState) => {
      const body = {
        name: form.name.trim(),
        kind: form.kind,
        timezone: form.timezone,
        values: form.values,
        description: form.description.trim() || null,
      };
      if (form.id) {
        return api<{ profile: ForecastProfile }>(`/forecasting/profiles/${form.id}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
      }
      return api<{ profile: ForecastProfile }>("/forecasting/profiles", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: (data) => {
      setProfileForm(profileToForm(data.profile));
      void queryClient.invalidateQueries({ queryKey: ["forecasting", "profiles"] });
      void queryClient.invalidateQueries({ queryKey: ["forecasting", "contracts"] });
    },
  });

  const deleteProfile = useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(`/forecasting/profiles/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setProfileForm(newProfileForm("BASELOAD"));
      void queryClient.invalidateQueries({ queryKey: ["forecasting", "profiles"] });
      void queryClient.invalidateQueries({ queryKey: ["forecasting", "contracts"] });
    },
  });

  const saveContract = useMutation({
    mutationFn: (form: ContractFormState) => {
      const body = contractBody(form);
      if (form.id) {
        return api<{ contract: ForecastContract }>(`/forecasting/contracts/${form.id}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
      }
      return api<{ contract: ForecastContract }>("/forecasting/contracts", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: (data) => {
      setContractForm(contractToForm(data.contract));
      void queryClient.invalidateQueries({ queryKey: ["forecasting", "contracts"] });
    },
  });

  const deleteContract = useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(`/forecasting/contracts/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setContractForm(newContractForm());
      void queryClient.invalidateQueries({ queryKey: ["forecasting", "contracts"] });
    },
  });

  const loading = profilesQuery.isLoading || contractsQuery.isLoading;
  const error =
    errorMessage(profilesQuery.error) ||
    errorMessage(contractsQuery.error) ||
    errorMessage(portfoliosQuery.error) ||
    errorMessage(saveProfile.error) ||
    errorMessage(deleteProfile.error) ||
    errorMessage(saveContract.error) ||
    errorMessage(deleteContract.error);

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold">{page.title}</h1>
          <div className="text-xs text-muted">{page.subtitle}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Contracts" value={String(contracts.length)} detail={`${stats.activeContracts} active`} />
        <Stat label="Active MW" value={formatMw(stats.activeMw)} detail="buy and sell absolute" />
        <Stat label="Profiles" value={String(profiles.length)} detail={`${stats.profileKinds} template types`} />
        <Stat label="Weekly MWh" value={formatNumber(stats.weeklyMwh, 1)} detail="profile-weighted active book" />
      </div>

      {loading && <div className="text-sm text-muted">Loading...</div>}
      {error && <div className="text-sm text-negRed">{error}</div>}

      {view === "contracts" ? (
        <ContractsView
          contracts={contracts}
          profiles={profiles}
          portfolios={portfolios}
          form={contractForm}
          setForm={setContractForm}
          onNew={() => setContractForm(newContractForm())}
          onSave={() => saveContract.mutate(contractForm)}
          onDelete={() => contractForm.id && deleteContract.mutate(contractForm.id)}
          saving={saveContract.isPending}
          deleting={deleteContract.isPending}
        />
      ) : view === "profiles" ? (
        <ProfilesView
          profiles={profiles}
          form={profileForm}
          setForm={setProfileForm}
          onNew={(kind) => setProfileForm(newProfileForm(kind))}
          onSave={() => saveProfile.mutate(profileForm)}
          onDelete={() => profileForm.id && deleteProfile.mutate(profileForm.id)}
          saving={saveProfile.isPending}
          deleting={deleteProfile.isPending}
        />
      ) : (
        <MonthView contracts={contracts} profiles={profiles} />
      )}
    </div>
  );
}

function ContractsView({
  contracts,
  profiles,
  portfolios,
  form,
  setForm,
  onNew,
  onSave,
  onDelete,
  saving,
  deleting,
}: {
  contracts: ForecastContract[];
  profiles: ForecastProfile[];
  portfolios: PortfolioOption[];
  form: ContractFormState;
  setForm: React.Dispatch<React.SetStateAction<ContractFormState>>;
  onNew: () => void;
  onSave: () => void;
  onDelete: () => void;
  saving: boolean;
  deleting: boolean;
}) {
  const selectedProfile = profiles.find((profile) => profile.id === form.profileId) ?? null;
  const projectedWeeklyMwh = selectedProfile ? profileWeightedHours(selectedProfile.values) * (Number(form.mw) || 0) : null;
  const canSave =
    form.name.trim() &&
    form.deliveryStartDate &&
    form.deliveryEndDate &&
    form.deliveryEndDate >= form.deliveryStartDate &&
    Number(form.mw) > 0 &&
    Number(form.price) >= 0;

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_27rem]">
      <section className="min-w-0 rounded-md border border-border/60">
        <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
          <div className="text-xs font-medium">Contract book</div>
          <button
            type="button"
            onClick={onNew}
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:border-accent"
          >
            <Plus className="h-3.5 w-3.5" />
            New
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-xs">
            <thead className="bg-border/20 text-muted">
              <tr>
                <Th>Name</Th>
                <Th>Side</Th>
                <Th>Period</Th>
                <Th>Profile</Th>
                <Th align="right">MW</Th>
                <Th align="right">Price</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((contract) => (
                <tr
                  key={contract.id}
                  className={cn(
                    "cursor-pointer border-t border-border/50 hover:bg-border/20",
                    form.id === contract.id && "bg-accent/10"
                  )}
                  onClick={() => setForm(contractToForm(contract))}
                >
                  <Td>
                    <div className="font-medium text-fg">{contract.name}</div>
                    <div className="text-[11px] text-muted">{contract.counterparty || "No counterparty"}</div>
                  </Td>
                  <Td>
                    <Pill tone={contract.side === "BUY" ? "buy" : "sell"}>{contract.side}</Pill>
                  </Td>
                  <Td>
                    {contract.deliveryStartDate} {"->"} {contract.deliveryEndDate}
                  </Td>
                  <Td>
                    <div>{contract.profileName ?? "No profile"}</div>
                    <div className="text-[11px] text-muted">{contract.profileKind ?? contract.area}</div>
                  </Td>
                  <Td align="right">{formatMw(contract.mw)}</Td>
                  <Td align="right">
                    {formatPrice(contract.price)} {contract.currency}/MWh
                  </Td>
                  <Td>
                    <Pill tone={contract.status === "ACTIVE" ? "active" : "neutral"}>{contract.status}</Pill>
                  </Td>
                </tr>
              ))}
              {contracts.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-muted">
                    No contracts defined.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-md border border-border/60">
        <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
          <div className="text-xs font-medium">{form.id ? "Edit contract" : "New contract"}</div>
          <button
            type="button"
            onClick={onNew}
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:border-accent"
            title="Start a new contract"
          >
            <Plus className="h-3.5 w-3.5" />
            New
          </button>
        </div>
        <div className="space-y-3 p-3">
          <TextField label="Name" value={form.name} onChange={(name) => setForm((f) => ({ ...f, name }))} />
          <TextField
            label="Counterparty"
            value={form.counterparty}
            onChange={(counterparty) => setForm((f) => ({ ...f, counterparty }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Side">
              <Segmented
                value={form.side}
                options={[
                  { value: "BUY", label: "Buy" },
                  { value: "SELL", label: "Sell" },
                ]}
                onChange={(side) => setForm((f) => ({ ...f, side: side as ContractSide }))}
              />
            </Field>
            <Field label="Status">
              <Select
                value={form.status}
                onChange={(status) => setForm((f) => ({ ...f, status: status as ContractStatus }))}
                options={["DRAFT", "ACTIVE", "ARCHIVED"].map((value) => ({ value, label: value }))}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              type="date"
              label="Start"
              value={form.deliveryStartDate}
              onChange={(deliveryStartDate) => setForm((f) => ({ ...f, deliveryStartDate }))}
            />
            <TextField
              type="date"
              label="End"
              value={form.deliveryEndDate}
              onChange={(deliveryEndDate) => setForm((f) => ({ ...f, deliveryEndDate }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              type="number"
              label="MW"
              value={form.mw}
              min="0"
              step="0.1"
              onChange={(mw) => setForm((f) => ({ ...f, mw }))}
            />
            <TextField
              type="number"
              label="Price"
              value={form.price}
              min="0"
              step="0.01"
              onChange={(price) => setForm((f) => ({ ...f, price }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Currency">
              <Segmented
                value={form.currency}
                options={[
                  { value: "EUR", label: "EUR" },
                  { value: "RON", label: "RON" },
                ]}
                onChange={(currency) => setForm((f) => ({ ...f, currency: currency as Currency }))}
              />
            </Field>
            <TextField label="Area" value={form.area} onChange={(area) => setForm((f) => ({ ...f, area }))} />
          </div>
          <Field label="Profile">
            <Select
              value={form.profileId}
              onChange={(profileId) => setForm((f) => ({ ...f, profileId }))}
              options={[
                { value: "", label: "No profile" },
                ...profiles.map((profile) => ({ value: profile.id, label: `${profile.name} · ${profile.kind}` })),
              ]}
            />
          </Field>
          <Field label="Portfolio">
            <Select
              value={form.portfolioId}
              onChange={(portfolioId) => setForm((f) => ({ ...f, portfolioId }))}
              options={[
                { value: "", label: "Unallocated" },
                ...portfolios.map((portfolio) => ({
                  value: portfolio.id,
                  label: `${portfolio.market} ${portfolio.brokerCode}${portfolio.shortName ? ` · ${portfolio.shortName}` : ""}`,
                })),
              ]}
            />
          </Field>
          <TextField
            label="Business line"
            value={form.businessLine}
            onChange={(businessLine) => setForm((f) => ({ ...f, businessLine }))}
          />
          <Field label="Notes">
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className="min-h-20 rounded border border-border bg-transparent px-2 py-1.5 text-sm outline-none focus:border-accent"
            />
          </Field>
          <div className="grid grid-cols-2 gap-2 rounded border border-border/60 bg-border/10 p-2 text-xs">
            <Metric label="Load factor" value={selectedProfile ? `${formatNumber(selectedProfile.loadFactor * 100, 1)}%` : "—"} />
            <Metric label="Weekly MWh" value={formatNumber(projectedWeeklyMwh, 1)} />
          </div>
          <div className="flex flex-wrap justify-between gap-2 pt-1">
            <button
              type="button"
              onClick={onDelete}
              disabled={!form.id || deleting}
              className="inline-flex items-center gap-2 rounded border border-border px-3 py-1.5 text-sm text-negRed hover:border-negRed disabled:opacity-40"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={!canSave || saving}
              className="inline-flex items-center gap-2 rounded border border-accent bg-accent px-3 py-1.5 text-sm text-bg disabled:opacity-40"
            >
              <Save className="h-4 w-4" />
              Save
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function MonthView({ contracts, profiles }: { contracts: ForecastContract[]; profiles: ForecastProfile[] }) {
  const [month, setMonth] = useState(() => defaultTemplateMonth());
  const [mode, setMode] = useState<MonthViewMode>("table");
  const [metric, setMetric] = useState<MonthMetric>("net");
  const [scope, setScope] = useState<ContractBookScope>("active");
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const monthOptions = useMemo(() => monthDropdownOptions(month, contracts), [month, contracts]);
  const book = useMemo(() => buildMonthBook(month, contracts, profiles, scope), [month, contracts, profiles, scope]);

  async function copyView() {
    await navigator.clipboard.writeText(monthBookToTsv(book, mode, metric));
    setCopyStatus("Copied");
    window.setTimeout(() => setCopyStatus(null), 1400);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded-md border border-border/60 p-3">
        <Field label="Month">
          <Select
            value={month}
            onChange={setMonth}
            options={monthOptions}
          />
        </Field>
        <Field label="Book">
          <Select
            value={scope}
            onChange={(next) => setScope(next as ContractBookScope)}
            options={[
              { value: "active", label: "Active" },
              { value: "draft-active", label: "Draft + active" },
              { value: "all", label: "All" },
            ]}
          />
        </Field>
        <Field label="Value">
          <Segmented
            value={metric}
            options={MONTH_METRICS}
            onChange={(next) => setMetric(next as MonthMetric)}
          />
        </Field>
        <Field label="View">
          <Segmented
            value={mode}
            options={MONTH_VIEW_MODES}
            onChange={(next) => setMode(next as MonthViewMode)}
          />
        </Field>
        <button
          type="button"
          onClick={() => void copyView()}
          disabled={book.rows.length === 0}
          className="inline-flex items-center gap-2 rounded border border-border px-3 py-1.5 text-sm hover:border-accent disabled:opacity-40"
        >
          <Copy className="h-4 w-4" />
          {copyStatus ?? "Copy TSV"}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Stat label="Intervals" value={formatNumber(book.rows.length, 0)} detail={`${book.days.length} days`} />
        <Stat
          label="Contracts"
          value={formatNumber(book.summary.contractsInMonth, 0)}
          detail={`${book.summary.unprofiledContracts} flat`}
        />
        <Stat label="Buy MWh" value={formatMw(book.summary.buyMwh)} detail="gross month" />
        <Stat label="Sell MWh" value={formatMw(book.summary.sellMwh)} detail="gross month" />
        <Stat label="Net MWh" value={formatSignedMw(book.summary.netMwh)} detail={monthDirectionLabel(book.summary.netMwh)} />
        <Stat label="Peak net MW" value={formatMw(book.summary.peakNetMw)} detail={`gross ${formatMw(book.summary.peakGrossMw)}`} />
      </div>

      {book.days.length === 0 ? (
        <div className="rounded-md border border-border/60 p-6 text-center text-sm text-muted">Select a valid month.</div>
      ) : mode === "table" ? (
        <MonthHourTable book={book} metric={metric} />
      ) : mode === "column" ? (
        <MonthColumnView book={book} />
      ) : (
        <MonthContractTotals rows={book.contractRows} />
      )}
    </div>
  );
}

function MonthHourTable({ book, metric }: { book: MonthBook; metric: MonthMetric }) {
  const maxAbs = Math.max(0, ...book.rows.map((row) => Math.abs(monthMetricValue(row, metric))));

  return (
    <div className="overflow-hidden rounded-md border border-border/60">
      <table className="w-full table-fixed border-collapse text-[10.5px]">
        <colgroup>
          <col className="w-[3.75rem]" />
          {book.days.map((day) => (
            <col key={day.date} />
          ))}
        </colgroup>
        <thead className="sticky top-0 z-10 bg-bg text-muted">
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">Hour</th>
            {book.days.map((day) => (
              <th key={day.date} className="border-l border-border/50 px-0.5 py-1.5 text-center font-medium">
                <div className="text-fg">{day.day}</div>
                <div className="text-[10px] leading-tight">{day.weekday}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {HOURS.map((hour) => (
            <tr key={hour} className="border-t border-border/50">
              <Td>
                <div className="font-mono text-fg">H{String(hour + 1).padStart(2, "0")}</div>
                <div className="text-[10px] text-muted">{timeIntervalLabel(hour)}</div>
              </Td>
              {book.days.map((day) => {
                const row = book.rows[day.index * 24 + hour]!;
                const value = monthMetricValue(row, metric);
                return (
                  <td
                    key={row.index}
                    className={cn(
                      "h-8 overflow-hidden truncate border-l border-border/40 px-0.5 text-right font-mono tabular-nums",
                      metric === "net" && value !== 0 && (value > 0 ? "text-posGreen" : "text-negRed"),
                      metric !== "net" && value !== 0 && "text-fg"
                    )}
                    style={{ backgroundColor: monthHeat(value, maxAbs, metric) }}
                    title={monthHourTitle(row)}
                  >
                    {formatMonthCell(value)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MonthColumnView({ book }: { book: MonthBook }) {
  return (
    <div className="max-h-[42rem] overflow-auto rounded-md border border-border/60">
      <table className="w-full min-w-[760px] text-xs">
        <thead className="sticky top-0 bg-bg text-muted">
          <tr>
            <Th align="right">#</Th>
            <Th>Date</Th>
            <Th>Hour</Th>
            <Th align="right">Buy MWh</Th>
            <Th align="right">Sell MWh</Th>
            <Th align="right">Net MWh</Th>
            <Th align="right">Contracts</Th>
          </tr>
        </thead>
        <tbody>
          {book.rows.map((row) => (
            <tr key={row.index} className="border-t border-border/50 hover:bg-border/20">
              <Td align="right">{row.index + 1}</Td>
              <Td>
                <div className="font-mono text-fg">{row.date}</div>
                <div className="text-[11px] text-muted">{row.weekday}</div>
              </Td>
              <Td>{hourLabel(row.hour)}</Td>
              <Td align="right">{formatMw(row.buyMw)}</Td>
              <Td align="right">{formatMw(row.sellMw)}</Td>
              <Td align="right">
                <span className={cn(row.netMw > 0 && "text-posGreen", row.netMw < 0 && "text-negRed")}>
                  {formatSignedMw(row.netMw)}
                </span>
              </Td>
              <Td align="right">{formatNumber(row.contractCount, 0)}</Td>
            </tr>
          ))}
          {book.rows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-3 py-8 text-center text-muted">
                No month intervals.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function MonthContractTotals({ rows }: { rows: MonthContractRow[] }) {
  return (
    <div className="overflow-auto rounded-md border border-border/60">
      <table className="w-full min-w-[940px] text-xs">
        <thead className="bg-border/20 text-muted">
          <tr>
            <Th>Contract</Th>
            <Th>Side</Th>
            <Th>Status</Th>
            <Th>Period</Th>
            <Th>Profile</Th>
            <Th align="right">MW</Th>
            <Th align="right">Hours</Th>
            <Th align="right">Buy MWh</Th>
            <Th align="right">Sell MWh</Th>
            <Th align="right">Net MWh</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.contract.id} className="border-t border-border/50 hover:bg-border/20">
              <Td>
                <div className="font-medium text-fg">{row.contract.name}</div>
                <div className="text-[11px] text-muted">{row.contract.counterparty || "No counterparty"}</div>
              </Td>
              <Td>
                <Pill tone={row.contract.side === "BUY" ? "buy" : "sell"}>{row.contract.side}</Pill>
              </Td>
              <Td>
                <Pill tone={row.contract.status === "ACTIVE" ? "active" : "neutral"}>{row.contract.status}</Pill>
              </Td>
              <Td>
                {row.contract.deliveryStartDate} {"->"} {row.contract.deliveryEndDate}
              </Td>
              <Td>
                <div>{row.profileName}</div>
                <div className="text-[11px] text-muted">{row.profileKind}</div>
              </Td>
              <Td align="right">{formatMw(row.contract.mw)}</Td>
              <Td align="right">{formatNumber(row.hourCount, 0)}</Td>
              <Td align="right">{formatMw(row.buyMwh)}</Td>
              <Td align="right">{formatMw(row.sellMwh)}</Td>
              <Td align="right">
                <span className={cn(row.netMwh > 0 && "text-posGreen", row.netMwh < 0 && "text-negRed")}>
                  {formatSignedMw(row.netMwh)}
                </span>
              </Td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={10} className="px-3 py-8 text-center text-muted">
                No contracts overlap this month.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ProfilesView({
  profiles,
  form,
  setForm,
  onNew,
  onSave,
  onDelete,
  saving,
  deleting,
}: {
  profiles: ForecastProfile[];
  form: ProfileFormState;
  setForm: React.Dispatch<React.SetStateAction<ProfileFormState>>;
  onNew: (kind: ProfileKind) => void;
  onSave: () => void;
  onDelete: () => void;
  saving: boolean;
  deleting: boolean;
}) {
  const [brush, setBrush] = useState(1);
  const summary = profileSummary(form.values);
  const canSave = form.name.trim().length > 0;

  return (
    <div className="grid gap-4 xl:grid-cols-[22rem_minmax(0,1fr)]">
      <section className="rounded-md border border-border/60">
        <div className="border-b border-border/60 px-3 py-2 text-xs font-medium">Profile library</div>
        <div className="space-y-3 p-3">
          <div className="grid grid-cols-2 gap-2">
            {PROFILE_KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                onClick={() => onNew(kind)}
                className="inline-flex items-center justify-center gap-1 rounded border border-border px-2 py-1.5 text-xs hover:border-accent"
              >
                <Wand2 className="h-3.5 w-3.5" />
                {kindLabel(kind)}
              </button>
            ))}
          </div>

          <div className="max-h-[34rem] overflow-y-auto border-t border-border/60 pt-2">
            {profiles.map((profile) => (
              <button
                key={profile.id}
                type="button"
                onClick={() => setForm(profileToForm(profile))}
                className={cn(
                  "mb-2 w-full rounded border border-border/60 p-2 text-left hover:border-accent",
                  form.id === profile.id && "border-accent bg-accent/10"
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{profile.name}</span>
                  <Pill tone="neutral">{profile.kind}</Pill>
                </div>
                <div className="mt-1 grid grid-cols-2 gap-2 text-[11px] text-muted">
                  <span>{formatNumber(profile.loadFactor * 100, 1)}% LF</span>
                  <span>{formatNumber(profile.activeHoursPerWeek, 1)} h/wk</span>
                </div>
              </button>
            ))}
            {profiles.length === 0 && <div className="py-6 text-center text-xs text-muted">No profiles saved.</div>}
          </div>
        </div>
      </section>

      <section className="min-w-0 rounded-md border border-border/60">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
          <div className="text-xs font-medium">{form.id ? "Edit profile" : "New profile"}</div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, values: fillValues(0) }))}
              className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:border-accent"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Clear
            </button>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, id: null, name: `${f.name} copy`, values: cloneValues(f.values) }))}
              className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:border-accent"
              title="Duplicate current shape into an unsaved profile"
            >
              <Copy className="h-3.5 w-3.5" />
              Duplicate
            </button>
          </div>
        </div>

        <div className="space-y-4 p-3">
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_12rem_11rem]">
            <TextField label="Name" value={form.name} onChange={(name) => setForm((f) => ({ ...f, name }))} />
            <Field label="Kind">
              <Select
                value={form.kind}
                onChange={(kind) => setForm((f) => ({ ...f, kind: kind as ProfileKind }))}
                options={PROFILE_KINDS.map((kind) => ({ value: kind, label: kindLabel(kind) }))}
              />
            </Field>
            <Field label="Timezone">
              <Select
                value={form.timezone}
                onChange={(timezone) => setForm((f) => ({ ...f, timezone: timezone as ForecastTimezone }))}
                options={[
                  { value: "CET", label: "CET" },
                  { value: "EET", label: "EET" },
                ]}
              />
            </Field>
          </div>

          <Field label="Description">
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="min-h-16 rounded border border-border bg-transparent px-2 py-1.5 text-sm outline-none focus:border-accent"
            />
          </Field>

          <div className="grid gap-2 rounded border border-border/60 bg-border/10 p-2 text-xs md:grid-cols-4">
            <Metric label="Load factor" value={`${formatNumber(summary.loadFactor * 100, 1)}%`} />
            <Metric label="Active hours" value={`${formatNumber(summary.activeHoursPerWeek, 1)} / week`} />
            <Metric label="Weighted hours" value={formatNumber(summary.weightedHoursPerWeek, 1)} />
            <Metric label="Brush" value={formatNumber(brush, 2)} />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="inline-flex overflow-hidden rounded border border-border text-xs">
              {BRUSH_VALUES.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setBrush(value)}
                  className={cn(
                    "h-8 min-w-12 px-2",
                    brush === value ? "bg-accent text-bg" : "hover:bg-border/30"
                  )}
                >
                  {value}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {PROFILE_KINDS.slice(0, 5).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      kind,
                      name: f.id ? f.name : kindLabel(kind),
                      values: templateValues(kind),
                    }))
                  }
                  className="rounded border border-border px-2 py-1 text-xs hover:border-accent"
                >
                  {kindLabel(kind)}
                </button>
              ))}
            </div>
          </div>

          <ProfileMatrix
            values={form.values}
            brush={brush}
            onChange={(values) => setForm((f) => ({ ...f, values }))}
          />

          <div className="flex flex-wrap justify-between gap-2 pt-1">
            <button
              type="button"
              onClick={onDelete}
              disabled={!form.id || deleting}
              className="inline-flex items-center gap-2 rounded border border-border px-3 py-1.5 text-sm text-negRed hover:border-negRed disabled:opacity-40"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={!canSave || saving}
              className="inline-flex items-center gap-2 rounded border border-accent bg-accent px-3 py-1.5 text-sm text-bg disabled:opacity-40"
            >
              <Save className="h-4 w-4" />
              Save
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function ProfileMatrix({
  values,
  brush,
  onChange,
}: {
  values: ProfileValues;
  brush: number;
  onChange: (values: ProfileValues) => void;
}) {
  const [isPainting, setIsPainting] = useState(false);

  useEffect(() => {
    if (!isPainting) return;
    const stopPainting = () => setIsPainting(false);
    window.addEventListener("mouseup", stopPainting);
    return () => window.removeEventListener("mouseup", stopPainting);
  }, [isPainting]);

  const paintHour = (day: number, hour: number) => {
    onChange(setHour(values, day, hour, brush));
  };

  return (
    <div className="overflow-x-auto rounded border border-border/60">
      <div className="min-w-[940px] p-2">
        <div className="grid grid-cols-[3rem_1fr] gap-1 text-[10px] text-muted">
          <div className="grid h-[3.25rem] grid-rows-3 items-center text-right font-mono text-[9px] leading-none">
            <span />
            <span>CET</span>
            <span>EET</span>
          </div>
          <div className="grid gap-0" style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}>
            {HOURS.map((hour) => (
              <div
                key={hour}
                className={cn(
                  "grid h-[3.25rem] grid-rows-3 items-center border-l border-border/35 px-0.5 text-center font-mono leading-none",
                  hour === 0 && "border-l-transparent"
                )}
              >
                <div className="text-[11px] text-fg">{hour + 1}</div>
                <div className="whitespace-nowrap text-[8.5px]">{timeIntervalLabel(hour)}</div>
                <div className="whitespace-nowrap text-[8.5px]">{timeIntervalLabel(hour + 1)}</div>
              </div>
            ))}
          </div>
          {DAYS.map((day, dayIdx) => (
            <div key={day} className="contents">
              <div className="flex h-7 items-center font-medium text-fg">{day}</div>
              <div className="grid gap-1" style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}>
                {HOURS.map((hour) => {
                  const value = hourAverage(values, dayIdx, hour);
                  const fill = value > 0 ? 0.12 + Math.min(value, 1) * 0.58 : 0;
                  return (
                    <button
                      key={`${day}-${hour}`}
                      type="button"
                      title={`${day} ${hour + 1} CET (${cetIntervalLabel(hour)}, ${eetIntervalLabel(hour)}) = ${formatNumber(value, 2)}`}
                      aria-label={`${day} hour ${hour + 1} CET, ${cetIntervalLabel(hour)}, ${eetIntervalLabel(hour)} profile value ${formatNumber(value, 2)}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setIsPainting(true);
                        paintHour(dayIdx, hour);
                      }}
                      onMouseEnter={() => {
                        if (isPainting) paintHour(dayIdx, hour);
                      }}
                      onClick={() => paintHour(dayIdx, hour)}
                      className={cn(
                        "h-7 rounded border text-[10px] font-mono tabular-nums transition-colors hover:border-accent focus:outline-none focus:ring-1 focus:ring-accent",
                        value > 0 ? "border-accent/40 text-fg" : "border-border/60 text-muted"
                      )}
                      style={{
                        backgroundColor: value > 0 ? `rgb(var(--accent) / ${fill})` : "transparent",
                      }}
                    >
                      {cellValueLabel(value)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Segmented({
  value,
  options,
  onChange,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded border border-border text-sm">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            "px-3 py-1.5",
            value === option.value ? "bg-accent text-bg" : "text-muted hover:bg-border/25 hover:text-fg"
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-muted">
      {label}
      {children}
    </label>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
  min,
  step,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  min?: string;
  step?: string;
}) {
  return (
    <Field label={label}>
      <input
        type={type}
        value={value}
        min={min}
        step={step}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-border bg-transparent px-2 py-1.5 text-sm text-fg outline-none focus:border-accent"
      />
    </Field>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded border border-border bg-bg px-2 py-1.5 text-sm text-fg outline-none focus:border-accent"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function Stat({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="rounded-md border border-border/60 p-3">
      <div className="text-xs text-muted">{label}</div>
      <div className="mt-1 font-mono text-lg text-fg">{value}</div>
      <div className="mt-1 text-[11px] text-muted">{detail}</div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] text-muted">{label}</div>
      <div className="font-mono text-sm text-fg">{value}</div>
    </div>
  );
}

function Pill({ tone, children }: { tone: "buy" | "sell" | "active" | "neutral"; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium",
        tone === "buy" && "border-posGreen/40 bg-posGreen/10 text-posGreen",
        tone === "sell" && "border-negRed/40 bg-negRed/10 text-negRed",
        tone === "active" && "border-accent/40 bg-accent/10 text-accent",
        tone === "neutral" && "border-border text-muted"
      )}
    >
      {children}
    </span>
  );
}

function Th({ children, align = "left" }: { children: React.ReactNode; align?: "left" | "right" }) {
  return <th className={cn("px-3 py-2 font-medium", align === "right" ? "text-right" : "text-left")}>{children}</th>;
}

function Td({ children, align = "left" }: { children: React.ReactNode; align?: "left" | "right" }) {
  return <td className={cn("px-3 py-2 align-middle", align === "right" ? "text-right" : "text-left")}>{children}</td>;
}

function forecastingBookPageMeta(view: ForecastContractsTab) {
  if (view === "profiles") {
    return {
      title: "Profile builder",
      subtitle: "Reusable baseload, peakload, offpeak, solar, BESS, and custom delivery shapes",
    };
  }
  if (view === "month") {
    return {
      title: "Month view",
      subtitle: "All contracts expanded into hourly month tables, interval columns, and contract totals",
    };
  }
  return {
    title: "Contracts",
    subtitle: "Hedge contract definitions, profiles, portfolios, and prices",
  };
}

type MonthDay = {
  index: number;
  date: string;
  day: number;
  weekday: string;
  weekdayIndex: number;
};

type MonthHourRow = {
  index: number;
  date: string;
  dayIndex: number;
  day: number;
  weekday: string;
  hour: number;
  buyMw: number;
  sellMw: number;
  netMw: number;
  contractCount: number;
};

type MonthContractRow = {
  contract: ForecastContract;
  profileName: string;
  profileKind: string;
  unprofiled: boolean;
  activeDays: number;
  hourCount: number;
  buyMwh: number;
  sellMwh: number;
  netMwh: number;
};

type MonthBook = {
  month: string;
  days: MonthDay[];
  rows: MonthHourRow[];
  contractRows: MonthContractRow[];
  summary: {
    contractsInMonth: number;
    unprofiledContracts: number;
    buyMwh: number;
    sellMwh: number;
    netMwh: number;
    peakNetMw: number;
    peakGrossMw: number;
  };
};

function buildMonthBook(
  month: string,
  contracts: ForecastContract[],
  profiles: ForecastProfile[],
  scope: ContractBookScope
): MonthBook {
  const days = monthDays(month);
  const rows = days.flatMap((day) =>
    HOURS.map((hour) => ({
      index: day.index * 24 + hour,
      date: day.date,
      dayIndex: day.index,
      day: day.day,
      weekday: day.weekday,
      hour,
      buyMw: 0,
      sellMw: 0,
      netMw: 0,
      contractCount: 0,
    }))
  );
  const profileById = new Map(profiles.map((profile) => [profile.id, profile]));
  const contractRows: MonthContractRow[] = [];

  for (const contract of contracts) {
    if (!contractInScope(contract, scope)) continue;
    const profile = contract.profileId ? profileById.get(contract.profileId) ?? null : null;
    const contractRow: MonthContractRow = {
      contract,
      profileName: profile?.name ?? "Flat",
      profileKind: profile?.kind ?? "No profile",
      unprofiled: !profile,
      activeDays: 0,
      hourCount: 0,
      buyMwh: 0,
      sellMwh: 0,
      netMwh: 0,
    };

    for (const day of days) {
      if (!contractOverlapsDate(contract, day.date)) continue;
      contractRow.activeDays += 1;
      for (const hour of HOURS) {
        const factor = contractHourFactor(profile, day.weekdayIndex, hour);
        if (factor <= 0) continue;
        const mwh = contract.mw * factor;
        const row = rows[day.index * 24 + hour]!;
        row.contractCount += 1;
        contractRow.hourCount += 1;
        if (contract.side === "BUY") {
          row.buyMw += mwh;
          contractRow.buyMwh += mwh;
        } else {
          row.sellMw += mwh;
          contractRow.sellMwh += mwh;
        }
        row.netMw = row.buyMw - row.sellMw;
      }
    }

    contractRow.netMwh = contractRow.buyMwh - contractRow.sellMwh;
    if (contractRow.activeDays > 0) contractRows.push(contractRow);
  }

  contractRows.sort((a, b) => {
    const sideRank = a.contract.side.localeCompare(b.contract.side);
    if (sideRank !== 0) return sideRank;
    return a.contract.name.localeCompare(b.contract.name);
  });

  const buyMwh = rows.reduce((sum, row) => sum + row.buyMw, 0);
  const sellMwh = rows.reduce((sum, row) => sum + row.sellMw, 0);

  return {
    month,
    days,
    rows,
    contractRows,
    summary: {
      contractsInMonth: contractRows.length,
      unprofiledContracts: contractRows.filter((row) => row.unprofiled).length,
      buyMwh,
      sellMwh,
      netMwh: buyMwh - sellMwh,
      peakNetMw: rows.reduce((max, row) => Math.max(max, Math.abs(row.netMw)), 0),
      peakGrossMw: rows.reduce((max, row) => Math.max(max, row.buyMw + row.sellMw), 0),
    },
  };
}

function monthDays(month: string): MonthDay[] {
  if (!/^\d{4}-\d{2}$/.test(month)) return [];
  const [year, monthNumber] = month.split("-").map(Number);
  if (!year || !monthNumber || monthNumber < 1 || monthNumber > 12) return [];
  const daysInMonth = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return Array.from({ length: daysInMonth }, (_, index) => {
    const day = index + 1;
    const date = `${year}-${String(monthNumber).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const weekdayIndex = weekdayIndexFromIsoDate(date);
    return {
      index,
      date,
      day,
      weekday: DAYS[weekdayIndex]!,
      weekdayIndex,
    };
  });
}

function monthDropdownOptions(selectedMonth: string, contracts: ForecastContract[]): Array<{ value: string; label: string }> {
  const currentMonth = defaultTemplateMonth();
  let fromMonth = addMonths(currentMonth, -12);
  let toMonth = addMonths(currentMonth, 24);

  for (const contract of contracts) {
    const startMonth = isoDateToMonth(contract.deliveryStartDate);
    const endMonth = isoDateToMonth(contract.deliveryEndDate);
    if (startMonth && startMonth < fromMonth) fromMonth = startMonth;
    if (endMonth && endMonth > toMonth) toMonth = endMonth;
  }

  if (isIsoMonth(selectedMonth)) {
    if (selectedMonth < fromMonth) fromMonth = selectedMonth;
    if (selectedMonth > toMonth) toMonth = selectedMonth;
  }

  return monthsBetween(fromMonth, toMonth).map((value) => ({
    value,
    label: monthOptionLabel(value),
  }));
}

function monthsBetween(fromMonth: string, toMonth: string): string[] {
  const months: string[] = [];
  let cursor = fromMonth;
  while (cursor <= toMonth && months.length < 180) {
    months.push(cursor);
    cursor = addMonths(cursor, 1);
  }
  return months;
}

function addMonths(month: string, offset: number): string {
  const [year, monthNumber] = month.split("-").map(Number);
  const date = new Date(Date.UTC(year!, monthNumber! - 1 + offset, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

function isoDateToMonth(date: string): string | null {
  const month = date.slice(0, 7);
  return isIsoMonth(month) ? month : null;
}

function isIsoMonth(month: string): boolean {
  return /^\d{4}-\d{2}$/.test(month);
}

function monthOptionLabel(month: string): string {
  const [year, monthNumber] = month.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" }).format(
    new Date(Date.UTC(year!, monthNumber! - 1, 1))
  );
}

function weekdayIndexFromIsoDate(date: string): number {
  const [year, month, day] = date.split("-").map(Number);
  const jsDay = new Date(Date.UTC(year!, month! - 1, day!)).getUTCDay();
  return (jsDay + 6) % 7;
}

function contractInScope(contract: ForecastContract, scope: ContractBookScope): boolean {
  if (scope === "active") return contract.status === "ACTIVE";
  if (scope === "draft-active") return contract.status === "ACTIVE" || contract.status === "DRAFT";
  return true;
}

function contractOverlapsDate(contract: ForecastContract, date: string): boolean {
  return contract.deliveryStartDate <= date && contract.deliveryEndDate >= date;
}

function contractHourFactor(profile: ForecastProfile | null, weekdayIndex: number, hour: number): number {
  if (!profile) return 1;
  const profileHour = profile.timezone === "EET" ? (hour + 1) % 24 : hour;
  const profileWeekdayIndex = profile.timezone === "EET" && hour === 23 ? (weekdayIndex + 1) % 7 : weekdayIndex;
  const values = profile.values[profileWeekdayIndex] ?? [];
  let sum = 0;
  for (let qh = profileHour * 4; qh < profileHour * 4 + 4; qh++) sum += Number(values[qh] ?? 0);
  return sum / 4;
}

function monthMetricValue(row: MonthHourRow, metric: MonthMetric): number {
  if (metric === "buy") return row.buyMw;
  if (metric === "sell") return row.sellMw;
  return row.netMw;
}

function monthHeat(value: number, maxAbs: number, metric: MonthMetric): string {
  if (!maxAbs || value === 0) return "transparent";
  const opacity = 0.08 + Math.min(Math.abs(value) / maxAbs, 1) * 0.24;
  if (metric === "net" && value < 0) return `rgb(var(--neg-red) / ${opacity})`;
  if (metric === "net" && value > 0) return `rgb(var(--pos-green) / ${opacity})`;
  return `rgb(var(--accent) / ${opacity})`;
}

function monthHourTitle(row: MonthHourRow): string {
  return [
    `${row.date} ${hourLabel(row.hour)}`,
    `Buy ${formatMw(row.buyMw)} MWh`,
    `Sell ${formatMw(row.sellMw)} MWh`,
    `Net ${formatSignedMw(row.netMw)} MWh`,
    `${formatNumber(row.contractCount, 0)} contracts`,
  ].join(" | ");
}

function hourLabel(hour: number): string {
  return `H${String(hour + 1).padStart(2, "0")} ${timeIntervalLabel(hour)}`;
}

function formatMonthCell(value: number): string {
  if (Math.abs(value) < 0.0005) return "";
  return formatNumber(value, 1);
}

function formatSignedMw(value: number): string {
  if (!Number.isFinite(value)) return "—";
  if (Math.abs(value) < 0.0005) return "0.0";
  return `${value > 0 ? "+" : "-"}${formatMw(Math.abs(value))}`;
}

function monthDirectionLabel(value: number): string {
  if (Math.abs(value) < 0.0005) return "flat";
  return value > 0 ? "long" : "short";
}

function monthBookToTsv(book: MonthBook, mode: MonthViewMode, metric: MonthMetric): string {
  if (mode === "table") {
    const header = ["hour", ...book.days.map((day) => day.date)];
    const rows = HOURS.map((hour) => [
      hourLabel(hour),
      ...book.days.map((day) => formatTsvNumber(monthMetricValue(book.rows[day.index * 24 + hour]!, metric))),
    ]);
    return [header, ...rows].map((row) => row.map(escapeTsv).join("\t")).join("\n");
  }

  if (mode === "contracts") {
    const rows = book.contractRows.map((row) => [
      row.contract.name,
      row.contract.side,
      row.contract.status,
      row.contract.deliveryStartDate,
      row.contract.deliveryEndDate,
      row.profileName,
      formatTsvNumber(row.contract.mw),
      formatTsvNumber(row.hourCount),
      formatTsvNumber(row.buyMwh),
      formatTsvNumber(row.sellMwh),
      formatTsvNumber(row.netMwh),
    ]);
    return [
      ["contract", "side", "status", "start", "end", "profile", "mw", "hours", "buy_mwh", "sell_mwh", "net_mwh"],
      ...rows,
    ]
      .map((row) => row.map(escapeTsv).join("\t"))
      .join("\n");
  }

  const rows = book.rows.map((row) => [
    String(row.index + 1),
    row.date,
    hourLabel(row.hour),
    formatTsvNumber(row.buyMw),
    formatTsvNumber(row.sellMw),
    formatTsvNumber(row.netMw),
    String(row.contractCount),
  ]);
  return [["index", "date", "hour", "buy_mwh", "sell_mwh", "net_mwh", "contracts"], ...rows]
    .map((row) => row.map(escapeTsv).join("\t"))
    .join("\n");
}

function formatTsvNumber(value: number): string {
  if (!Number.isFinite(value)) return "";
  return String(Math.round(value * 1000000) / 1000000);
}

function escapeTsv(value: string): string {
  return value.replace(/[\t\r\n]+/g, " ");
}

function newProfileForm(kind: ProfileKind): ProfileFormState {
  return {
    id: null,
    name: kindLabel(kind),
    kind,
    timezone: "CET",
    values: templateValues(kind),
    description: "",
  };
}

function profileToForm(profile: ForecastProfile): ProfileFormState {
  return {
    id: profile.id,
    name: profile.name,
    kind: profile.kind,
    timezone: normalizeForecastTimezone(profile.timezone),
    values: cloneValues(profile.values),
    description: profile.description ?? "",
  };
}

function newContractForm(): ContractFormState {
  const today = new Date().toISOString().slice(0, 10);
  return {
    id: null,
    name: "",
    counterparty: "",
    side: "BUY",
    status: "DRAFT",
    profileId: "",
    portfolioId: "",
    deliveryStartDate: today,
    deliveryEndDate: today,
    mw: "",
    price: "",
    currency: "EUR",
    area: "TEL",
    businessLine: "",
    notes: "",
  };
}

function defaultTemplateMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function contractToForm(contract: ForecastContract): ContractFormState {
  return {
    id: contract.id,
    name: contract.name,
    counterparty: contract.counterparty,
    side: contract.side,
    status: contract.status,
    profileId: contract.profileId ?? "",
    portfolioId: contract.portfolioId ?? "",
    deliveryStartDate: contract.deliveryStartDate,
    deliveryEndDate: contract.deliveryEndDate,
    mw: String(contract.mw),
    price: String(contract.price),
    currency: contract.currency,
    area: contract.area,
    businessLine: contract.businessLine ?? "",
    notes: contract.notes ?? "",
  };
}

function contractBody(form: ContractFormState) {
  return {
    name: form.name.trim(),
    counterparty: form.counterparty.trim(),
    side: form.side,
    status: form.status,
    profileId: form.profileId || null,
    portfolioId: form.portfolioId || null,
    deliveryStartDate: form.deliveryStartDate,
    deliveryEndDate: form.deliveryEndDate,
    mw: Number(form.mw),
    price: Number(form.price),
    currency: form.currency,
    area: form.area.trim() || "TEL",
    businessLine: form.businessLine.trim() || null,
    notes: form.notes.trim() || null,
  };
}

function templateValues(kind: ProfileKind): ProfileValues {
  if (kind === "BASELOAD") return fillValues(1);
  if (kind === "CUSTOM") return fillValues(0);
  return Array.from({ length: 7 }, (_, day) =>
    Array.from({ length: 96 }, (_, qh) => {
      const hour = qh / 4;
      const weekday = day < 5;
      const peak = weekday && hour >= 8 && hour < 20;
      if (kind === "PEAKLOAD") return peak ? 1 : 0;
      if (kind === "OFFPEAK") return peak ? 0 : 1;
      if (kind === "SOLAR") {
        if (hour < 6 || hour > 20) return 0;
        return round(Math.sin(((hour - 6) / 14) * Math.PI));
      }
      if (kind === "BESS") return hour >= 17 && hour < 22 ? 1 : hour >= 7 && hour < 10 ? 0.35 : 0;
      return 0;
    })
  );
}

function fillValues(value: number): ProfileValues {
  return Array.from({ length: 7 }, () => Array.from({ length: 96 }, () => value));
}

function cloneValues(values: ProfileValues): ProfileValues {
  return values.map((day) => [...day]);
}

function setHour(values: ProfileValues, day: number, hour: number, value: number): ProfileValues {
  const next = cloneValues(values);
  for (let qh = hour * 4; qh < hour * 4 + 4; qh++) next[day]![qh] = value;
  return next;
}

function hourAverage(values: ProfileValues, day: number, hour: number): number {
  const row = values[day] ?? [];
  let sum = 0;
  for (let qh = hour * 4; qh < hour * 4 + 4; qh++) sum += row[qh] ?? 0;
  return sum / 4;
}

function cellValueLabel(value: number): string {
  if (value === 0) return "";
  if (value === 1) return "1";
  return String(value).replace(/^0/, "");
}

function cetIntervalLabel(hour: number): string {
  return `${timeIntervalLabel(hour)} CET`;
}

function eetIntervalLabel(hour: number): string {
  return `${timeIntervalLabel(hour + 1)} EET`;
}

function timeIntervalLabel(hour: number): string {
  return `${clockStart(hour)}-${clockEnd(hour + 1)}`;
}

function clockStart(hour: number): string {
  return String(hour % 24).padStart(2, "0");
}

function clockEnd(hour: number): string {
  return hour === 24 ? "24" : String(hour % 24).padStart(2, "0");
}

function profileSummary(values: ProfileValues) {
  const flat = values.flat();
  const weightedHoursPerWeek = flat.reduce((sum, value) => sum + value * 0.25, 0);
  return {
    loadFactor: weightedHoursPerWeek / (7 * 24),
    activeHoursPerWeek: flat.filter((value) => value > 0).length * 0.25,
    weightedHoursPerWeek,
  };
}

function profileWeightedHours(values: ProfileValues): number {
  return profileSummary(values).weightedHoursPerWeek;
}

function contractStats(contracts: ForecastContract[], profiles: ForecastProfile[]) {
  const activeContracts = contracts.filter((contract) => contract.status === "ACTIVE");
  return {
    activeContracts: activeContracts.length,
    activeMw: activeContracts.reduce((sum, contract) => sum + Math.abs(contract.mw), 0),
    profileKinds: new Set(profiles.map((profile) => profile.kind)).size,
    weeklyMwh: activeContracts.reduce((sum, contract) => sum + (contract.estimatedWeeklyMwh ?? 0), 0),
  };
}

function kindLabel(kind: ProfileKind): string {
  return kind
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function errorMessage(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : String(error);
}

function normalizeForecastTimezone(value: string): ForecastTimezone {
  return value === "EET" ? "EET" : "CET";
}
